import React, { useEffect, useState } from "react";
import useRefetchOnFocus from "../../../hooks/useRefetchOnFocus";
import "./GcDashboard.scss";
import Button from "../../../Components/Common/Button/Button";
import { useNavigate } from "react-router-dom";
import { useGcLayout } from "../GcLayout";
import useGetProjects from "../../../hooks/useGetProjects";
import useScrollToTop from "../../../hooks/useScrollToTop";
import ProjectCardSkeleton from "../../../Components/Common/Skeleton/ProjectCardSkeleton";
import CommercialIcon from "Assets/Img/CommercialIcon";
import ResidentialIcon from "Assets/Img/ResidentialIcon";
import Pagination from "../../../Components/Common/Pagination/Pagination";
import NetworkError from "Components/Common/NetworkError/NetworkError";
import EmptyState from "Components/Common/EmptyState/EmptyState";
import EmptyProjectsIcon from "Assets/Img/EmptyProjectsIcon";
import { formatStatus } from "../GcProjectDetails/components/DrawHistoryTable";

interface Project {
  id: string;
  name: string;
  type: "residential" | "commercial" | string;
  bank: string;
  status: string;
  projectStage: string;
  isGcAcknowledge: boolean;
  admin: { firstName: string; lastName: string };
  contractAmount: number;
  currencySymbol: string;
}

const PRECON_STATUSES = new Set(["pre_construction", "draw_request"]);
const PAGE_SIZE = 10;

export const STATUS_LABELS: Record<string, string> = {
  pre_construction: "Pre-con pending",
  draw_request: "Draw Pending",
};

const STAGE_BUTTON_LABELS: Record<string, string> = {
  gc_requirements: "GC Requirement →",
  gc_pre_construction_checklist: "Complete Checklist →",
  gc_sov_submission: "Complete SOV →",
};

const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

const getStatusLabel = (status: string) =>
  STATUS_LABELS[status] ?? formatStatus(status);

const GcDashboard = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const { setCrumbs } = useGcLayout();

  useEffect(() => {
    window.history.scrollRestoration = "manual";
    setCrumbs([{ label: "My Projects" }]);
    return () => {
      window.history.scrollRestoration = "auto";
    };
  }, []);

  useScrollToTop(page, { skipFirstRender: false });

  const { projects, pagination, loading, error, refetch } = useGetProjects(
    page,
    PAGE_SIZE,
  );

  useRefetchOnFocus(refetch);
  const preconProjects: Project[] = projects.filter((p: Project) =>
    PRECON_STATUSES.has(p.status),
  );
  const activeProjects: Project[] = projects.filter(
    (p: Project) => !PRECON_STATUSES.has(p.status),
  );
  const checklistProject = preconProjects.find(
    (p) => p.projectStage === "gc_pre_construction_checklist",
  );

  const navigateToStage = (project: Project) => {
    switch (project.projectStage) {
      case "gc_requirements":
        navigate(`/gc-requirements/${project.id}`);
        break;
      case "gc_pre_construction_checklist":
        navigate(`/gc-precon/${project.id}`, {
          state: { preconType: project.type, projectData: project },
        });
        break;
      case "gc_sov_submission":
        localStorage.setItem(
          "gcSovData",
          JSON.stringify({
            contractAmount: project.contractAmount,
            currencySymbol: project.currencySymbol,
            bank: project.bank,
          }),
        );
        navigate(`/gc-sov/${project.id}`);
        break;
      default:
        navigate(`/gc-sov/${project.id}`);
    }
  };

  const renderActionButton = (project: Project) => {
    const label = STAGE_BUTTON_LABELS[project.projectStage];
    if (!label) return null;
    return (
      <Button
        className="btn btnSm btnPrimary"
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation();
          navigateToStage(project);
        }}
      >
        {label}
      </Button>
    );
  };

  return (
    <div className="gcDashboard">
      <div className="dashLayout">
        <div className="dashHeader">
          <div>
            <h1>My Projects</h1>
            <p>
              {loading
                ? " "
                : preconProjects.length > 0
                  ? `You have ${preconProjects.length} action${preconProjects.length > 1 ? "s" : ""} requiring your attention.`
                  : "All projects are up to date."}
            </p>
          </div>
          <div>
            <Button
              className="btn btnSm btnPrimary"
              onClick={() => navigate("/sub-contractor")}
            >
              Manage Subcontractor
            </Button>
          </div>
        </div>

        {error && <NetworkError />}

        {loading && (
          <>
            <div className="sectionLabel">Loading</div>
            {Array.from({ length: PAGE_SIZE }).map((_, i) => (
              <ProjectCardSkeleton key={i} />
            ))}
          </>
        )}

        {!loading && !error && projects.length === 0 && (
          <EmptyState
            height={"500px"}
            icon={<EmptyProjectsIcon />}
            title={"No Projects Yet"}
            subtitle={
              "Projects added to the platform will appear here once they are created."
            }
          />
        )}

        {!loading && !error && checklistProject && (
          <div className="notifBanner">
            <div className="notifIcon">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path
                  d="M9 2a5 5 0 00-5 5v2.5L2.5 12h13L14 9.5V7a5 5 0 00-5-5z"
                  stroke="#fff"
                  strokeWidth="1.3"
                  fill="none"
                />
                <path
                  d="M7 13.5a2 2 0 004 0"
                  stroke="#fff"
                  strokeWidth="1.3"
                  fill="none"
                />
              </svg>
            </div>
            <div className="notifBody">
              <div className="notifTitle">
                Pre-Construction Checklist Required —{" "}
                {capitalize(checklistProject.type)} Project
              </div>
              <div className="notifText">
                {checklistProject.admin?.firstName}{" "}
                {checklistProject.admin?.lastName} has set up a new{" "}
                {checklistProject.type} project and your pre-construction
                document submission is required. Please review the checklist and
                submit all required documents before the deadline.
              </div>
              <div className="notifAction">
                <Button
                  className="btn btnSm notifActionButton"
                  onClick={() => navigateToStage(checklistProject)}
                >
                  View Checklist →
                </Button>
              </div>
            </div>
          </div>
        )}

        {!loading && !error && preconProjects.length > 0 && (
          <>
            <div className="sectionLabel">Action Required</div>
            {preconProjects.map((project: Project) => (
              <div
                key={project.id}
                className="projectCard projectCardActionRequired"
                onClick={() =>
                  project.status === "draw_request"
                    ? navigate(`/gc-project-detail/${project.id}`)
                    : !renderActionButton(project) && navigateToStage(project)
                }
              >
                <div className="pcThumb">
                  {project.type === "commercial" ? (
                    <CommercialIcon />
                  ) : (
                    <ResidentialIcon />
                  )}
                </div>
                <div className="pcInfo">
                  <div className="pcName">{project?.name}</div>
                  <div className="pcMeta">
                    {project?.bank && <>{project?.bank} · </>}
                    {capitalize(project?.type)}
                  </div>
                </div>
                <div className="pcStatus">
                  <span className="badge badgeAmber">
                    <span className="badgeDot" />
                    {getStatusLabel(project?.status)}
                  </span>
                  {renderActionButton(project)}
                </div>
              </div>
            ))}
          </>
        )}

        {!loading && !error && activeProjects.length > 0 && (
          <>
            <div className="sectionLabel sectionLabelWithTopMargin">
              Active Projects
            </div>
            {activeProjects.map((project) => (
              <div
                key={project?.id}
                className="projectCard"
                onClick={() => navigate(`/gc-project-detail/${project.id}`)}
              >
                <div className="pcThumb">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <rect
                      x="2"
                      y="7"
                      width="20"
                      height="15"
                      rx="1"
                      stroke="#14508c"
                      strokeWidth="1.3"
                      fill="none"
                    />
                    <path
                      d="M6 7V4a1 1 0 011-1h10a1 1 0 011 1v3"
                      stroke="#14508c"
                      strokeWidth="1.3"
                      fill="none"
                    />
                  </svg>
                </div>
                <div className="pcInfo">
                  <div className="pcName">{project?.name}</div>
                  <div className="pcMeta">
                    {project.bank && <>{project?.bank} · </>}
                    {capitalize(project?.type)}
                  </div>
                </div>
                <div className="pcStatus">
                  <span className="badge badgeGreen">
                    <span className="badgeDot" />
                    {getStatusLabel(project.status)}
                  </span>
                </div>
              </div>
            ))}
          </>
        )}
        {pagination.totalPages > 1 && (
          <Pagination
            currentPage={page}
            totalPages={pagination.totalPages}
            total={pagination.total}
            limit={PAGE_SIZE}
            onPageChange={setPage}
          />
        )}
      </div>
    </div>
  );
};

export default GcDashboard;
